import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import { Settings, Save } from 'lucide-react';
import toast from 'react-hot-toast';

const AttributeMapping = ({ config, onSave }) => {
  const [mapping, setMapping] = useState({
    email: 'email',
    firstName: 'firstName',
    lastName: 'lastName',
    role: 'role'
  });
  const [roleMapping, setRoleMapping] = useState({
    admin: 'admin',
    member: 'member',
    owner: 'owner'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (config?.attribute_mapping) {
      setMapping(config?.attribute_mapping);
    }
    if (config?.settings?.roleMapping) {
      setRoleMapping(config?.settings?.roleMapping);
    }
  }, [config]);

  const handleMappingChange = (field, value) => {
    setMapping(prev => ({ ...prev, [field]: value }));
  };

  const handleRoleMappingChange = (ssoRole, appRole) => {
    setRoleMapping(prev => ({ ...prev, [ssoRole]: appRole }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(mapping);
      toast?.success('Attribute mapping updated successfully');
    } catch (error) {
      toast?.error('Failed to update attribute mapping');
    } finally {
      setLoading(false);
    }
  };

  const commonAttributes = [
    { value: 'email', label: 'Email Address' },
    { value: 'mail', label: 'Mail' },
    { value: 'emailAddress', label: 'Email Address (camelCase)' },
    { value: 'firstName', label: 'First Name' },
    { value: 'givenName', label: 'Given Name' },
    { value: 'lastName', label: 'Last Name' },
    { value: 'surname', label: 'Surname' },
    { value: 'familyName', label: 'Family Name' },
    { value: 'displayName', label: 'Display Name' },
    { value: 'name', label: 'Full Name' },
    { value: 'role', label: 'Role' },
    { value: 'groups', label: 'Groups' },
    { value: 'memberOf', label: 'Member Of' }
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Settings className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Attribute Mapping</h2>
            <p className="text-sm text-muted-foreground">
              Map SSO attributes to DraftIQ user fields
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Basic Attribute Mapping */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-4">User Attributes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Email Mapping */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <select
                value={mapping?.email}
                onChange={(e) => handleMappingChange('email', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {commonAttributes?.filter(attr => 
                  ['email', 'mail', 'emailAddress']?.includes(attr?.value)
                )?.map(attr => (
                  <option key={attr?.value} value={attr?.value}>
                    {attr?.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                SSO attribute containing user email
              </p>
            </div>

            {/* First Name Mapping */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                First Name
              </label>
              <select
                value={mapping?.firstName}
                onChange={(e) => handleMappingChange('firstName', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {commonAttributes?.filter(attr => 
                  ['firstName', 'givenName']?.includes(attr?.value)
                )?.map(attr => (
                  <option key={attr?.value} value={attr?.value}>
                    {attr?.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                SSO attribute containing first name
              </p>
            </div>

            {/* Last Name Mapping */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Last Name
              </label>
              <select
                value={mapping?.lastName}
                onChange={(e) => handleMappingChange('lastName', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {commonAttributes?.filter(attr => 
                  ['lastName', 'surname', 'familyName']?.includes(attr?.value)
                )?.map(attr => (
                  <option key={attr?.value} value={attr?.value}>
                    {attr?.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                SSO attribute containing last name
              </p>
            </div>

            {/* Role Mapping */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Role / Groups
              </label>
              <select
                value={mapping?.role}
                onChange={(e) => handleMappingChange('role', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {commonAttributes?.filter(attr => 
                  ['role', 'groups', 'memberOf']?.includes(attr?.value)
                )?.map(attr => (
                  <option key={attr?.value} value={attr?.value}>
                    {attr?.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                SSO attribute containing role or group membership
              </p>
            </div>
          </div>
        </div>

        {/* Role Mapping */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-4">Role Assignment</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Map SSO roles/groups to DraftIQ team member roles
          </p>
          <div className="space-y-3">
            {['admin', 'member', 'owner']?.map(appRole => (
              <div key={appRole} className="flex items-center gap-4">
                <div className="w-32">
                  <span className="text-sm font-medium text-foreground capitalize">
                    {appRole}
                  </span>
                </div>
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder={`SSO role/group for ${appRole}`}
                    value={roleMapping?.[appRole] || ''}
                    onChange={(e) => handleRoleMappingChange(appRole, e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Enter the exact SSO role or group name that should map to each DraftIQ role
          </p>
        </div>

        {/* Preview */}
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-sm font-semibold text-foreground mb-3">Mapping Preview</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email:</span>
              <span className="font-mono text-foreground">{mapping?.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">First Name:</span>
              <span className="font-mono text-foreground">{mapping?.firstName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Last Name:</span>
              <span className="font-mono text-foreground">{mapping?.lastName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Role:</span>
              <span className="font-mono text-foreground">{mapping?.role}</span>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            loading={loading}
            disabled={loading}
            iconName="Save"
          >
            Save Attribute Mapping
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AttributeMapping;